const settings = {
    cssProperties: {
        // Overall scale, used for sizing the white background and
        // positioning the small circles
        scale: '--scale',

        // This could support rotating these and other transitions as well
        offsetBlue: '--offset-blue',
        offsetRed: '--offset-red',
        offsetYellow: '--offset-yellow',
    },
};

const elementTypes = {
    whiteCircle: 0,
    blueCircle: 1,
    redCircle: 2,
    yellowCircle: 3,
};

const smallCircleElementTypes = [
    elementTypes.blueCircle,
    elementTypes.redCircle,
    elementTypes.yellowCircle,
];

export default class CircleScreensBackground {
    /**
     * @constructor
     * @param {HTMLElement} container
     */
    constructor(container) {
        this.container = container;
        if (!this.container) {
            return;
        }

        this.white = this.container.querySelector('.circle-screen-background__white');
        this.blue = this.container.querySelector('.circle-screen-background__blue');
        this.red = this.container.querySelector('.circle-screen-background__red');
        this.yellow = this.container.querySelector('.circle-screen-background__yellow');
    }

    static get elementTypes() {
        return elementTypes;
    }

    /**
     * Transitions the opacity of a circle element
     * @param {number} elementType
     * @param {number} opacity
     * @param {object} timeline
     * @param {number} startTime
     * @param {number} duration
     * @param {string} easing
     */
    setOpacity(
        elementType,
        opacity,
        timeline,
        startTime,
        duration,
        easing,
    ) {
        const elementInfo = new ElementInfo(this, elementType);

        // Opacity
        timeline.to(
            elementInfo.element,
            duration,
            {
                ease: easing,
                opacity: opacity,
            },
            startTime,
        );
    }

    /**
     * Transitions the scale of the component
     * @param {number} scale
     * @param {object} timeline
     * @param {number} startTime
     * @param {number} duration
     * @param {string} easing
     */
    transitionScale(
        scale,
        timeline,
        startTime,
        duration,
        easing,
    ) {
        // Scale
        const tweenOptions = {
            ease: easing,
        };
        tweenOptions[settings.cssProperties.scale] =
            scale;

        timeline.to(
            this.container,
            duration,
            tweenOptions,
            startTime,
        );
    }

    /**
     * Transitions the opacity and offset of a circle element
     * @param {number} elementType
     * @param {number} opacity
     * @param {number} offset
     * @param {object} timeline
     * @param {number} startTime
     * @param {number} duration
     * @param {string} easing
     */
    transitionSmallCircle(
        elementType,
        opacity,
        offset,
        timeline,
        startTime,
        duration,
        easing,
    ) {
        const elementInfo = new ElementInfo(this, elementType);
        if (!elementInfo.isSmallCircle) {
            throw new Error();
        }

        // Opacity
        if (opacity !== null) {
            timeline.to(
                elementInfo.element,
                duration,
                {
                    ease: easing,
                    opacity: opacity,
                },
                startTime,
            );
        }

        // Moving outward
        if (offset !== null) {
            const tweenOptions = {
                ease: easing,
            };
            tweenOptions[elementInfo.offsetCssProperty] = offset;

            timeline.from(
                this.container,
                duration,
                tweenOptions,
                startTime,
            );
        }
    }
}

/*
    Internal classes and functions
*/

/** Encapsulates settings for an element of this component */
class ElementInfo {
    /**
     * @constructor
     * @param {CircleScreensBackground} background
     * @param {number} elementType
     */
    constructor(
        background,
        elementType,
    ) {
        this.elementType = elementType;

        switch (elementType) {
            case elementTypes.whiteCircle:
                this.element = background.white;
                break;

            case elementTypes.blueCircle:
                this.element = background.blue;
                this.offsetCssProperty = settings.cssProperties.offsetBlue;
                break;

            case elementTypes.redCircle:
                this.element = background.red;
                this.offsetCssProperty = settings.cssProperties.offsetRed;
                break;

            case elementTypes.yellowCircle:
                this.element = background.yellow;
                this.offsetCssProperty = settings.cssProperties.offsetYellow;
                break;

            default:
                throw new Error();
        }
    }

    get isSmallCircle() {
        return smallCircleElementTypes.includes(this.elementType);
    }
}
